import {
  Catalog,
  CommercialProductSubType,
  EppCategory,
  OnlineModelCategory,
  VirtualCatalogStatus,
} from '../../generated/api/models.js';
import { StatusColor } from '../enums.js';
import { draftMsg, elisaDevicesServiceMsg, modifiedMsg, monthMsg, publishedMsg, t } from '../i18n/index.js';
import { categories } from './categoryUtils.js';
import { isDefined } from './objectUtils.js';
import { formatSum } from './priceUtils.js';
import { VAT_MULTIPLIER } from './taxUtils.js';
import type { ProductGridItem } from '../../components/ProductGrid/ProductGrid.js';
import type {
  CatalogHeader,
  OfferBadge,
  OnlineModelHeader,
  VirtualCatalog,
  VirtualCatalogHeader,
} from '../../generated/api/models.js';

export interface CatalogProduct {
  code: string;
  category?: OnlineModelCategory;
  created: number;
  imageUrl: string;
  isPriceLoading?: boolean;
  manufacturer: string;
  monthlyRecurringCharge?: number;
  name: string;
  offerBadge?: OfferBadge;
  oneTimeCharge?: number;
  payments?: number;
  pagePath?: string;
  tags: Array<string>;
  ref?: OnlineModelHeader;
}

export type SupportedModelCategory = keyof typeof SupportedModelCategory;

// PHONE, TABLET, COMPUTERS, NETWORKEQUIPMENT, ACCESSORIES are allowed to show up on online layer yet.
// eslint-disable-next-line no-redeclare
export const SupportedModelCategory = {
  PHONE: OnlineModelCategory.PHONE as 'PHONE',
  TABLET: OnlineModelCategory.TABLET as 'TABLET',
  COMPUTERS: OnlineModelCategory.COMPUTERS as 'COMPUTERS',
  NETWORK_EQUIPMENT: OnlineModelCategory.NETWORK_EQUIPMENT as 'NETWORK_EQUIPMENT',
  ACCESSORIES: OnlineModelCategory.ACCESSORIES as 'ACCESSORIES',
} as const;

export const SupportedModelCategories: readonly SupportedModelCategory[] = Object.values(SupportedModelCategory);

export interface ProductRecommendation {
  code: string;
  recommendedAccessories?: Record<AccessoryCategory, CatalogProduct[]>;
}

export enum AccessoryTag {
  SCREEN_COVERS = 'näytönsuojat',
  PROTECTIVE_CASES = 'suojalaukut ja -kotelot',
  PROTECTIVE_COVERS = 'suojakuoret',
  CHARGERS = 'laturit',
}

export enum AccessoryCategory {
  SCREEN_COVERS = 'screenCovers',
  CASES = 'cases',
  CHARGERS = 'chargers',
  OTHERS = 'others',
}

export const ContractPeriod = {
  MONTHS_12: 12,
  MONTHS_24: 24,
  MONTHS_36: 36,
  MONTHS_48: 48,
};

const calculatePrice = (
  model: OnlineModelHeader,
  catalog: Partial<Catalog>,
  isEmployeeUser: boolean
): { lowestOneTimeCharge?: number; lowestRecurringCharge?: number } => {
  let lowestOneTimeCharge: number | undefined;
  let lowestRecurringCharge: number | undefined;

  // Filter prices as per catalog
  const lowestPrices =
    catalog.productType === Catalog.ProductTypeEnum.EPP_RECURRING
      ? model.lowestPrices.find(price => price.productSubType === CommercialProductSubType.EPP_DEVICE)?.prices
      : model.lowestPrices.find(price => price.productSubType === CommercialProductSubType.DEVICE)?.prices;
  if (
    catalog.productType === Catalog.ProductTypeEnum.EPP_RECURRING ||
    catalog.productType === Catalog.ProductTypeEnum.RECURRING
  ) {
    lowestRecurringCharge = lowestPrices?.find(
      price => price.monthlyRecurringCharge && price.payments === catalog.contractPeriod
    )?.monthlyRecurringCharge;
  } else if (catalog.productType === Catalog.ProductTypeEnum.ONETIME) {
    lowestOneTimeCharge = lowestPrices?.find(price => price.oneTimeCharge)?.oneTimeCharge;
  }
  if (!lowestRecurringCharge && !lowestOneTimeCharge) {
    return { lowestOneTimeCharge: undefined, lowestRecurringCharge: undefined };
  }

  // Adjust corporate share
  if (isEmployeeUser) {
    if (catalog.productType === Catalog.ProductTypeEnum.EPP_RECURRING) {
      if (catalog.corporateShare) {
        lowestRecurringCharge = Math.max(lowestRecurringCharge! - catalog.corporateShare, 0);
      } else {
        lowestRecurringCharge = 0;
      }
    } else if (catalog.productType === Catalog.ProductTypeEnum.RECURRING) {
      lowestRecurringCharge = 0;
    } else if (catalog.productType === Catalog.ProductTypeEnum.ONETIME) {
      lowestOneTimeCharge = 0;
    }
  }

  // Add tax
  if (isEmployeeUser) {
    if (lowestOneTimeCharge) {
      lowestOneTimeCharge = Math.round(lowestOneTimeCharge * VAT_MULTIPLIER);
    }
    if (lowestRecurringCharge) {
      lowestRecurringCharge = Math.round(lowestRecurringCharge * VAT_MULTIPLIER);
    }
  }

  return {
    lowestOneTimeCharge,
    lowestRecurringCharge,
  };
};

const calculatePriceForAccessories = (
  model: OnlineModelHeader,
  catalog: Partial<Catalog>,
  isEmployeeUser: boolean
): { lowestOneTimeCharge?: number; lowestRecurringCharge?: number } => {
  const lowestOneTimeCharge = model.lowestPrices
    .map(lowestPrice => lowestPrice.prices)
    .reduce((x, y) => x.concat(y), [])
    ?.find(price => price.oneTimeCharge)?.oneTimeCharge;
  let lowestRecurringCharge: number | undefined;

  // Find lowest price from model
  const lowestPrices = model.lowestPrices.map(lowestPrice => lowestPrice.prices).reduce((x, y) => x.concat(y), []);
  // Only one time priced accessories if catalog is EPP_RECURRING type
  if (catalog.productType !== Catalog.ProductTypeEnum.EPP_RECURRING) {
    const recurringCharges = lowestPrices
      ?.filter(price => price.monthlyRecurringCharge)
      .map(price => price.monthlyRecurringCharge!);
    if (recurringCharges.length > 0) {
      lowestRecurringCharge = Math.min(...recurringCharges);
    }
  }

  // Assuming accessories won't have only EPP prices, make employee share 0
  if (isEmployeeUser) {
    return {
      lowestOneTimeCharge: lowestOneTimeCharge === undefined ? undefined : 0,
      lowestRecurringCharge: lowestRecurringCharge === undefined ? undefined : 0,
    };
  }

  return {
    lowestOneTimeCharge: lowestOneTimeCharge,
    lowestRecurringCharge: lowestRecurringCharge,
  };
};

export const toDisplayableProduct = (
  model: OnlineModelHeader,
  catalog: Partial<Catalog>,
  isEmployeeUser: boolean,
  filterProducts: boolean
): ProductGridItem | null => {
  // Filter product as per catalog
  if (
    filterProducts &&
    (!catalog.products?.includes(model.onlineModelCode) || (catalog.products && catalog.products.length === 0))
  ) {
    return null;
  }

  // Calculate price
  const { lowestOneTimeCharge, lowestRecurringCharge } =
    model.category === OnlineModelCategory.ACCESSORIES
      ? calculatePriceForAccessories(model, catalog, isEmployeeUser)
      : calculatePrice(model, catalog, isEmployeeUser);
  const contractPeriod = model.category === OnlineModelCategory.ACCESSORIES ? undefined : catalog.contractPeriod;

  // Model does not contain price type needed by catalog config
  if (lowestRecurringCharge === undefined && lowestOneTimeCharge === undefined) {
    return null;
  }

  return {
    code: model.onlineModelCode,
    category: model.category,
    name: model.onlineModelName,
    manufacturer: model.manufacturer,
    imageUrl: model.listingImage,
    oneTimeCharge: lowestOneTimeCharge,
    monthlyRecurringCharge: lowestRecurringCharge,
    payments: contractPeriod,
    pagePath: model.pagePath,
    created: model.created,
    tags: model.tags || [],
  };
};

export const getSelectedCatalog = (catalogs?: Catalog[], selectedCatalogCode?: string | null) =>
  selectedCatalogCode !== undefined
    ? catalogs?.find(catalog => catalog.catalogCode === selectedCatalogCode)
    : undefined;

export const collectProductCategoriesOfVirtualCatalog = (vCatalog: VirtualCatalog): Array<OnlineModelCategory> =>
  (vCatalog.draft?.productCategories || []).concat(vCatalog.published?.productCategories || []);

// TODO: Correct return type in below
export const findCatalogById = (
  virtualCatalogs: VirtualCatalog[] = [],
  catalogId?: string | null,
  onlyPublished?: boolean
) => {
  if (catalogId) {
    const catalog = virtualCatalogs.find(
      ({ published, draft }) =>
        published?.catalogCode === catalogId || (!onlyPublished && draft?.catalogCode === catalogId)
    );
    return catalog?.published || catalog?.draft;
  }

  return undefined;
};

export const productTypeDetails = (): { [s in Catalog.ProductTypeEnum]: { displayValue: string } } => ({
  ONETIME: {
    displayValue: t.XED2('Device subject to one-time fee'),
  },
  RECURRING: {
    displayValue: t.OF9U('Devices subject to monthly fee'),
  },
  EPP_RECURRING: {
    displayValue: t.TRE5(elisaDevicesServiceMsg),
  },
});

export const damageInsuranceItem = [
  { displayValue: () => 'Business Pro', valueRef: EppCategory.BUSINESS_PRO },
  { displayValue: () => 'Business Premium', valueRef: EppCategory.BUSINESS_PREMIUM },
];

export const getDamageInsuranceDisplayableItems = (items: EppCategory[]): string =>
  items.map(item1 => damageInsuranceItem.find(item => item.valueRef === item1)?.displayValue()).join(', ');

export const getCorporateShareAsText = (corporateShare?: number) => {
  return corporateShare != null ? `${formatSum(corporateShare)}/${t.XXVX(monthMsg)}` : '—';
};

export const getCatalogStatus = (catalog: VirtualCatalog | VirtualCatalogHeader): VirtualCatalogStatus => {
  if (catalog.published && catalog.draft) {
    return VirtualCatalogStatus.MODIFIED;
  } else if (catalog.published && !catalog.draft) {
    return VirtualCatalogStatus.ACTIVE;
  } else {
    return VirtualCatalogStatus.DRAFT;
  }
};

interface CatalogModificationStatus {
  color: StatusColor;
  text: string;
  subtext?: string;
}

export const getCatalogModificationStatus = (status: VirtualCatalogStatus): CatalogModificationStatus => {
  switch (status) {
    case 'ACTIVE':
      return { color: StatusColor.GREEN, text: t.JN25(publishedMsg) };
    case 'DRAFT':
      return { color: StatusColor.GREY, text: t.ZJQG(draftMsg), subtext: t.L8A5('Unpublished') };
    case 'MODIFIED':
      return { color: StatusColor.YELLOW, text: t.RTJW(modifiedMsg), subtext: t.BQMC('Unpublished edits') };
  }
};

export const getPublishedCatalogs = (virtualCatalogs?: VirtualCatalog[]): Catalog[] | undefined => {
  return virtualCatalogs?.map(e => e.published).filter(isDefined);
};

export function getCopyVersionOfCatalog(catalog: Catalog): Catalog {
  return {
    ...catalog,
    billingAccountId: undefined,
    selectedBillingAccountIds: [],
    catalogCode: undefined,
    created: undefined,
    lastModified: undefined,
    publishedStatus: undefined,
    versionNumber: undefined,
  };
}

// TODO: Optimize below to process per category, instead of all category in a go.
export const toCatalogProductsPerCategory = (
  catalog: Partial<Catalog>,
  filterProducts: boolean,
  onlineModelHeaders?: Record<string, OnlineModelHeader[]> | null,
  isPriceLoading?: (onlineModelCode: string) => boolean
): Record<string, Array<CatalogProduct>> => {
  const products: Record<string, Array<CatalogProduct>> = {};
  if (onlineModelHeaders) {
    const productCategories = filterProducts ? catalog.productCategories : SupportedModelCategories;
    if (productCategories) {
      productCategories.forEach(category => {
        if (onlineModelHeaders[category]) {
          products[category] = onlineModelHeaders[category]
            .map(model => {
              const product = toDisplayableProduct(model, catalog, false, filterProducts);
              return product
                ? {
                    ...product,
                    isPriceLoading: isPriceLoading?.(product.code) ?? false,
                  }
                : null;
            })
            .filter(isDefined);
        }
      });
    }
  }
  return products;
};

export const resolveCategoryUrl = (catalogs: Catalog[], catalogCode: string) => {
  const currentCatalog = catalogs.find(catalog => catalog.catalogCode === catalogCode);
  const catalogProductCategories = currentCatalog ? currentCatalog.productCategories : [];

  if (!catalogProductCategories.includes(OnlineModelCategory.PHONE) && catalogProductCategories.length > 0) {
    const firstProductCategory = catalogProductCategories[0];
    return Object.values(categories).find(category => category?.code === firstProductCategory)?.urlPath;
  }

  return categories.PHONE?.urlPath;
};

export const getCatalogToBeDisplayed = (virtualCatalog: VirtualCatalogHeader): CatalogHeader | undefined =>
  virtualCatalog.draft || virtualCatalog.published;
